import React, { useEffect, useMemo, useRef, useState } from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { BrowserRouter } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { setLogoutCallback } from "./auth/logoutHandler";
import { userService } from "./services/userService";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import "./App.css";
import AppRoutes from "./routes";
import Layout from "./components/Layout/Layout";
import { ProSidebarProvider } from "react-pro-sidebar";
import { CONFIG } from "./config";
import { queryClient } from "./services/queryClient";
import { SnackbarProvider } from "./contexts/SnackbarContext";

function App() {
  const interval = useRef(null);

  const { user, isLoading: authLoading } = useAuth0();

  // Always fetch fresh token when user is present - don't trust stale localStorage
  const [isTokenReady, setIsTokenReady] = useState(false);

  const [themeMode, setThemeMode] = useState(
    localStorage.getItem("theme") || CONFIG.defaultTheme,
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  const { getAccessTokenSilently, logout } = useAuth0();
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode === "dark" ? "dark" : "light",
          gradient:
            themeMode === "dark"
              ? "linear-gradient( to bottom, #191B1F 0%, #191B1F 550px, #25282D 550px, #25282D 150%)"
              : "linear-gradient( to bottom, " +
                CONFIG.primaryColour +
                " 0%, " +
                CONFIG.primaryColour +
                " 550px, #EFEFEF 550px , #EFEFEF 150%)",
          primary: {
            main: CONFIG.primaryColour,
          },
          secondary: {
            main: CONFIG.secondaryColour,
          },
        },
        typography: {
          fontFamily: "Plus Jakarta Sans",
        },
      }),
    [themeMode],
  );

  const handleThemeChange = (mode = null) => {
    if (mode !== null) {
      setThemeMode(mode);
      localStorage.setItem("theme", mode);
      return;
    }

    if (localStorage.getItem("theme") === null) {
      setThemeMode(CONFIG.defaultTheme === "light" ? "dark" : "light");
      localStorage.setItem(
        "theme",
        CONFIG.defaultTheme === "light" ? "dark" : "light",
      );
    } else if (localStorage.getItem("theme") === "light") {
      setThemeMode("dark");
      localStorage.setItem("theme", "dark");
    } else {
      setThemeMode("light");
      localStorage.setItem("theme", "light");
    }
  };

  const [collapsed, setCollapsed] = useState(
    localStorage.getItem("sidebarCollapsed") === "true" ? true : false,
  );

  const handleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const fetchTokenAndRefreshData = async () => {
    try {
      let accessToken = await getAccessTokenSilently();

      localStorage.setItem("access_token", accessToken);
      setIsTokenReady(true);
      // React Query will automatically fetch data as needed
      queryClient.invalidateQueries();
    } catch (e) {
      console.log(e.message);
    }
  };

  // Register logout so API interceptor can log out on 403 Admin required (stable callback, ref avoids loop)
  useEffect(() => {
    setLogoutCallback((opts) =>
      logoutRef.current?.(opts ?? { returnTo: window.location.origin }),
    );
    return () => setLogoutCallback(null);
  }, []);

  // Use useEffect to run side effects after render
  useEffect(() => {
    if (user) {
      const initializeApp = async () => {
        // fetch user access token and store it in local storage - MUST complete before API calls
        await fetchTokenAndRefreshData();

        // Verify current user is admin (GET /user/me). Non-admins get 403 and are logged out by the API interceptor.
        userService.getMe().catch(() => {
          // 403 is handled by api interceptor (logout + snackbar)
        });
      };

      initializeApp();

      // Set up an interval to refresh the token and data every 30 seconds
      interval.current = setInterval(() => {
        fetchTokenAndRefreshData();
      }, 30000);

      //event listener to detect if mobile
      window.addEventListener("resize", () => {
        if (window.innerWidth < 900) {
          setIsMobile(true);
        } else {
          setIsMobile(false);
        }
      });
    }

    // Clean up function to run when the component unmounts
    return () => {
      clearInterval(interval.current);
    };
  }, [user]);

  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {authLoading || (user && !isTokenReady) ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              Loading...
            </div>
          ) : (
            <BrowserRouter>
              <ProSidebarProvider>
                <Layout
                  handleThemeChange={handleThemeChange}
                  themeMode={themeMode}
                  handleCollapsed={handleCollapsed}
                  collapsed={collapsed}
                  isMobile={isMobile}
                >
                  <AppRoutes />
                </Layout>
              </ProSidebarProvider>
            </BrowserRouter>
          )}
        </ThemeProvider>
      </SnackbarProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
